import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  AuthService,
  LicenseGeneralFeature,
  LicenseProductType,
  LicenseRetrieve,
  LicenseService,
  LicenseTestFeature,
  LinksService,
  User,
} from '@isophi/core-legacy';
import { BaseComponent, IconService } from '@isophi/core-ui';
import { Menu } from '@isophi/teachers-shared';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, shareReplay, Subscription, switchMap, takeUntil } from 'rxjs';

import { RouterLinkFactory } from '../../../core/router/router-link.factory';
import { KindergartenService } from '../../../core/services/kindergarten.service';
import { MenuService, MenuType } from '../services/menu.service';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const EXPIRING_WARNING_DAYS = 30;

type LicenseBannerType = 'trial-expired' | 'license-expiring' | 'license-expired';

interface LicenseBannerState {
  type: LicenseBannerType;
  daysLeft?: number;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('mainDropdownButton') mainDropdownButton: ElementRef<HTMLElement>;

  @ViewChild('mainDropdown') mainDropdown: ElementRef<HTMLElement>;

  loggedUser: User;

  licenses$: Observable<{
    generalSupport: boolean;
    test_3_4: LicenseRetrieve;
    test_4_5: LicenseRetrieve;
    test_5_7_v2: LicenseRetrieve;
  }>;

  menuData$: Observable<Menu[]>;

  loggedUserEditLink: (string | number)[];

  protected readonly icons = inject(IconService);

  protected readonly links = inject(RouterLinkFactory);

  private readonly authService = inject(AuthService);

  private readonly licenseService = inject(LicenseService);

  private readonly router = inject(Router);

  private readonly menuService = inject(MenuService);

  private readonly transloco = inject(TranslocoService);

  private readonly cd = inject(ChangeDetectorRef);

  private readonly kindergartenService = inject(KindergartenService);

  private readonly linksService = inject(LinksService);

  protected readonly infoBannerData$ = new BehaviorSubject<{
    show: boolean;
    daysUntilTrialEnds: number;
    hasTestKit: boolean;
  }>({
    show: false,
    daysUntilTrialEnds: 0,
    hasTestKit: false,
  });

  protected licenseBanner: LicenseBannerState | null = null;

  private _subs: Subscription[] = [];

  isDirector = this.authService.loggedUser?.teacher.isDirector;

  protected readonly routeToManagementAddUser =
    !this.kindergartenService.loggedKindergarten.isSK() && !this.kindergartenService.loggedKindergarten.isSchool();

  protected readonly managementAddUserUrl = this.linksService.getManagementAppUrl({ redirect: '/settings/user/list' });

  ngOnInit(): void {
    this.loggedUser = this.authService.loggedUser;
    this.loggedUserEditLink = this.links.editTeacher(this.authService.loggedUser.teacher.id);
    this._loadMenu();

    this._subs.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.mainDropdownButton.nativeElement.classList.remove('show');
          this.mainDropdown.nativeElement.classList.remove('show');
        }
      }),
      this.transloco.langChanges$.subscribe(() => this._loadMenu())
    );
    this.initLicenses();
    this.initLicenseBanner();
  }

  get username(): string {
    return this.authService.loggedUser?.email;
  }

  /**
   * Init licenses for this component.
   * Based on them some navigation items are shown/hidden.
   */
  private initLicenses(): void {
    this.licenses$ = combineLatest({
      // Check if there are any support materials, videos etc. for the products user has
      generalSupport: this.licenseService.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.GENERAL_SUPPORT),
      test_3_4: this.licenseService.getLicenseStatus(LicenseProductType.TEST_3_4, LicenseGeneralFeature.TRIAL),
      test_4_5: this.licenseService.getLicenseStatus(LicenseProductType.TEST_4_5, LicenseGeneralFeature.TRIAL),
      test_5_7_v2: this.licenseService.getLicenseStatus(LicenseProductType.TEST_5_7_v2, LicenseGeneralFeature.TRIAL),
    }).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    combineLatest([
      this.licenses$.pipe(filter((licenses) => !!licenses)),
      // Check if any test_kit (physical product) exists
      combineLatest([
        this.licenseService.hasProductWithFeature(LicenseProductType.TEST_3_4, LicenseGeneralFeature.TEST_KIT),
        this.licenseService.hasProductWithFeature(LicenseProductType.TEST_4_5, LicenseGeneralFeature.TEST_KIT),
        this.licenseService.hasProductWithFeature(LicenseProductType.TEST_5_7_v2, LicenseGeneralFeature.TEST_KIT),
      ]).pipe(map((testKits) => testKits.some((val) => !!val))),
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([licences, hasTestKit]) => {
        const days =
          Math.min(...[licences.test_3_4.daysUntilEnd || 0, licences.test_4_5.daysUntilEnd || 0, licences.test_5_7_v2.daysUntilEnd || 0]) ||
          0;
        this.infoBannerData$.next({
          show: (licences.test_3_4.active || licences.test_4_5.active || licences.test_5_7_v2.active) && days > 0,
          daysUntilTrialEnds: days,
          hasTestKit: hasTestKit,
        });
      });
  }

  private _loadMenu(): void {
    this.menuData$ = this.menuService.getMenu(MenuType.TOP);
    this.cd.markForCheck();
  }

  get isDemoKindergarten(): boolean {
    return this.kindergartenService.loggedKindergarten.isDemo;
  }

  /**
   * Check expirable licenses for diagnostics (ONLINE_REPORT_GROUP on all products under 'test' type).
   * Shows a banner 30 days before expiration, after expiration, or when trial expired without a paid license.
   * Priority: license-expired > license-expiring > trial-expired.
   */
  private initLicenseBanner(): void {
    this.licenseService
      .getProductsByType(LicenseProductType.TEST)
      .pipe(
        switchMap((productCodenames) => {
          if (productCodenames.length === 0) return of([]);
          return combineLatest(
            productCodenames.map((codename) =>
              combineLatest([
                this.licenseService.getLicenseStatus(codename, LicenseTestFeature.ONLINE_REPORT_GROUP),
                this.licenseService.getLicenseStatus(codename, LicenseGeneralFeature.TRIAL),
              ]).pipe(map(([groupReport, trial]) => ({ groupReport, trial })))
            )
          );
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((allProducts) => {
        const now = new Date();
        let banner: LicenseBannerState | null = null;

        for (const p of allProducts) {
          const trialActive = p.trial?.active || false;
          const trialValidTo = p.trial?.validTo ? new Date(p.trial.validTo) : null;
          const trialExpired = trialValidTo ? now.getTime() > trialValidTo.getTime() : false;

          const paidValidTo = p.groupReport?.validTo ? new Date(p.groupReport.validTo) : null;
          const hasSeparatePaidLicense = paidValidTo && trialValidTo
            ? paidValidTo.getTime() > trialValidTo.getTime()
            : !!(paidValidTo && !trialValidTo);

          // Skip products where trial is still active (handled by trial banner)
          if (trialActive) continue;

          if (hasSeparatePaidLicense && paidValidTo) {
            const isAfterExpiration = now.getTime() > paidValidTo.getTime();
            if (isAfterExpiration) {
              if (!banner || banner.type !== 'license-expired') {
                banner = { type: 'license-expired' };
              }
            } else {
              const daysToExpiration = Math.ceil((paidValidTo.getTime() - now.getTime()) / ONE_DAY_MS);
              if (daysToExpiration > 0 && daysToExpiration <= EXPIRING_WARNING_DAYS) {
                if (!banner || (banner.type !== 'license-expired' && (!banner.daysLeft || daysToExpiration < banner.daysLeft))) {
                  banner = { type: 'license-expiring', daysLeft: daysToExpiration };
                }
              }
            }
          } else if (trialExpired && !banner) {
            banner = { type: 'trial-expired' };
          }
        }

        this.licenseBanner = banner;
        this.cd.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this._subs.forEach((_sub) => _sub.unsubscribe());
  }
}
